// FLAG:useDocuAi — pure builder for the invoice preview-variant switcher (original / XML / report /
// validation report / attachments). Additive Layer-A util with no flag check inside; it is only ever consumed by
// the gated document-carousel-wrapper seam, so flag-OFF never reaches it. Re-derived from the oracle
// (ralf/dms-xmlhandling). Combines the document's own `generatedFiles` (legacy omxweb generated files)
// with DMS-owned `invoiceRelatedFiles` (extracted XML, business-terms report, embedded attachments) and
// synthesizes the KoSIT validation report from `validationResult` (served by the existing
// GET /file/:id/validation/report route).
import lang from '@/lang'

export interface InvoiceRelatedFile {
  id: string
  decodedName?: string
  name?: string
  mimetype?: string
  url?: string
  createdAt?: string
  minioId?: string
  dmsVersionId?: string
  isDmsRelatedFile?: boolean
  relationKind?: string
  extractorSource?: string
  originalPath?: string
}

export interface InvoicePreviewDocument extends InvoiceRelatedFile {
  displayLabel: string
  isGeneratedFile?: boolean
  isValidationReport?: boolean
  validationReportFileId?: string
  // FLAG:useDocuAi — carried onto every variant (not just Original) so the valid/invalid e-invoice
  // hint stays visible no matter which preview tab is active.
  validationResult?: Record<string, unknown>
  metaData?: {
    zoom?: number
    rotate?: number
  }
  tags?: unknown[]
  tenantId?: unknown
}

export interface InvoicePreviewSource extends InvoiceRelatedFile {
  fileId?: string
  generatedFiles?: InvoiceRelatedFile[]
  invoiceRelatedFiles?: InvoiceRelatedFile[]
  validationResult?: Record<string, unknown>
  tenantId?: unknown
}

export const getInvoicePreviewFileName = (file: { decodedName?: string, name?: string }) =>
  file.decodedName || file.name || ''

export const isInvoiceSignatureFile = (file: { decodedName?: string, name?: string }) =>
  getInvoicePreviewFileName(file).toLowerCase().includes('_signed')

export const getInvoicePreviewDocumentKindLabel = (doc: Partial<InvoicePreviewDocument>, index: number) => {
  if (index === 0) return lang.documents.previewCarouselWrapperTabs.original

  const name = getInvoicePreviewFileName(doc)
  const lowerName = name.toLowerCase()
  const relationKind = doc.relationKind || ''

  // relationKind first (authoritative for DMS related files) — the validation-report's filename
  // contains both "report" and "xml", so name-matching alone would mislabel the validation report.
  if (doc.isValidationReport || relationKind === 'validation-report') {
    return lang.documents.previewCarouselWrapperTabs.validationReport
  }
  if (relationKind === 'business-terms-report') return lang.documents.previewCarouselWrapperTabs.report
  if (relationKind === 'extracted-xml') return lang.documents.previewCarouselWrapperTabs.xml
  if (relationKind === 'embedded-attachment') return `Anlage: ${name}`

  if (lowerName.includes('report')) return lang.documents.previewCarouselWrapperTabs.report
  if (lowerName.includes('xml')) return lang.documents.previewCarouselWrapperTabs.xml

  return name || `${index + 1}`
}

const getSortOrder = (file: InvoiceRelatedFile) => {
  const relationKind = file.relationKind || ''
  const name = getInvoicePreviewFileName(file).toLowerCase()
  if (relationKind === 'extracted-xml' || name.includes('xml')) return 0
  if (relationKind === 'business-terms-report' || name.includes('report')) return 2
  if (relationKind === 'embedded-attachment') return 3
  if (name.includes('_signed')) return 999
  return 2
}

const toPreviewDocument = (
  file: InvoiceRelatedFile,
  document: InvoicePreviewSource,
  index: number,
): InvoicePreviewDocument => {
  // The KoSIT validation-report artifact is an SVRL XML envelope with the human-readable validation report
  // embedded as an <html> block. Tag it text/html + isValidationReport so the preview slices out and
  // renders that HTML (DocumentDrawerPreviewHtml) instead of showing the raw XML envelope.
  const isValidationReport = file.relationKind === 'validation-report'
  return {
    id: String(file.id),
    name: getInvoicePreviewFileName(file),
    decodedName: getInvoicePreviewFileName(file),
    displayLabel: getInvoicePreviewDocumentKindLabel(file, index),
    mimetype: isValidationReport ? 'text/html' : file.mimetype,
    isValidationReport,
    url: file.url,
    minioId: file.minioId,
    dmsVersionId: file.dmsVersionId,
    isGeneratedFile: !file.isDmsRelatedFile,
    isDmsRelatedFile: file.isDmsRelatedFile,
    relationKind: file.relationKind,
    extractorSource: file.extractorSource,
    originalPath: file.originalPath,
    createdAt: file.createdAt,
    metaData: {
      zoom: file.mimetype === 'application/pdf' ? 2 : 1,
      rotate: 0,
    },
    tags: [],
    tenantId: document.tenantId,
    validationResult: document.validationResult,
  }
}

// FLAG:useDocuAi — search parent-rollup preselect: resolve which switcher variant a
// matched child (DMS version id) maps to. Returns the variant's id, or null when the
// child isn't among the built documents (e.g. relation deleted) — the caller then
// keeps the default (Original). The Original entry (index 0) is never preselected via
// this path: it is matched only if its own dmsVersionId equals the child id, which
// cannot happen for a real child.
export const resolvePreselectedDocumentId = (
  displayDocuments: Array<Partial<InvoicePreviewDocument>>,
  preselectChildVersionId?: string | null,
): string | null => {
  if (!preselectChildVersionId) return null
  const match = displayDocuments.find(doc => doc.dmsVersionId && doc.dmsVersionId === preselectChildVersionId)
  return match?.id != null ? String(match.id) : null
}

// Build the ordered list of preview documents for the switcher:
// Original → supporting (XML, report) → validation report → attachments.
// Signature files are pulled out and returned separately (rendered via the existing signature toggle).
export const buildInvoicePreviewDocuments = (document: InvoicePreviewSource) => {
  const docs: InvoicePreviewDocument[] = [{
    ...document,
    id: String(document.id || document.fileId),
    displayLabel: lang.documents.previewCarouselWrapperTabs.original,
  } as InvoicePreviewDocument]

  const seen = new Set<string>([
    document.dmsVersionId,
    document.minioId,
    String(document.id || ''),
    String(document.fileId || ''),
  ].filter(Boolean) as string[])

  const allRelatedFiles = [
    ...(document.invoiceRelatedFiles || []),
    ...(document.generatedFiles || []),
  ]

  const sortedRelatedFiles = [...allRelatedFiles].sort((a, b) => getSortOrder(a) - getSortOrder(b))
  const signatureDocument = sortedRelatedFiles.find(isInvoiceSignatureFile)
  const supportingDocs: InvoicePreviewDocument[] = []
  const attachmentDocs: InvoicePreviewDocument[] = []

  sortedRelatedFiles
    .filter(file => !isInvoiceSignatureFile(file))
    .forEach((file) => {
      const key = file.dmsVersionId || file.minioId || String(file.id)
      if (seen.has(key)) return
      seen.add(key)
      const previewDocument = toPreviewDocument(file, document, docs.length + supportingDocs.length + attachmentDocs.length)
      if (file.relationKind === 'embedded-attachment') {
        attachmentDocs.push(previewDocument)
      } else {
        supportingDocs.push(previewDocument)
      }
    })

  docs.push(...supportingDocs)

  // The DMS-owned validation-report relation (rendered as HTML by toPreviewDocument's
  // isValidationReport tagging) is the authoritative validation report. Only fall back to synthesizing one
  // from validationResult — pointing at the legacy /validation/report route, which only ever serves
  // xmlValidationResult (the OUTGOING-invoice path) — when no such relation exists, to avoid a
  // duplicate dropdown entry that 404s for DocuAI-analysed incoming invoices.
  const hasDmsValidationReport = supportingDocs.some(doc => doc.relationKind === 'validation-report')

  // …and only when that legacy route actually HAS something to serve. `hasStoredReport` is set by
  // serialize.js exclusively for `xmlValidationResult`, the OUTGOING-invoice path this route reads.
  // A DocuAI-analysed INCOMING invoice also carries a `validationResult` (the valid/invalid
  // verdict), but its report lives in the DMS relation above — so when the relations are missing
  // (e.g. the cross-cluster load failed) synthesising an entry here produced a "validation report" that
  // could only ever fail to load. Observed on mars: the dropdown showed Original + a validation report that
  // never came. Better to offer nothing than something broken.
  const canServeSynthesizedReport = document?.validationResult?.hasStoredReport === true

  if (document?.validationResult && canServeSynthesizedReport && !hasDmsValidationReport) {
    const fileId = String(document.fileId || document.id)
    docs.push({
      id: `${fileId}-validation-report`,
      name: 'validation-report.html',
      decodedName: lang.documents.previewCarouselWrapperTabs.validationReport,
      displayLabel: lang.documents.previewCarouselWrapperTabs.validationReport,
      mimetype: 'text/html',
      url: document.url ? `${document.url}/validation/report` : undefined,
      isValidationReport: true,
      validationReportFileId: fileId,
      createdAt: '',
      metaData: {
        zoom: 1,
        rotate: 0,
      },
      tags: [],
      tenantId: document.tenantId,
      validationResult: document.validationResult,
    })
  }

  docs.push(...attachmentDocs)

  return { displayDocuments: docs, signatureDocument }
}
